// =============================================================================
// costTracker — fire-and-forget insert do llm_call_events
// =============================================================================
// Wolany na koncu kazdego provider.call()/callResponses(). Zapisuje:
//   - provider, model, step, tokens (input/output/cached/reasoning)
//   - cost_usd (computed przez estimateCost w providerze)
//   - context (map_id, node_id, run_id) jesli options je podal
//   - duration_ms, user_id (z bieżącej sesji Supabase)
//
// Fire-and-forget = nigdy nie blokuje LLM response. Błąd inserta tylko loguje
// przez contentLogger.logDebug, nie propaguje.

import { supabase } from "@/integrations/supabase/client";
import { logDebug } from "../contentEngine/contentLogger";
import type { LLMProviderType } from "./types";

export interface LlmCallEvent {
  provider: LLMProviderType;
  model: string;
  step?: string;
  tokensInput: number;
  tokensOutput: number;
  cachedTokens?: number;
  reasoningTokens?: number;
  costUsd: number;
  durationMs?: number;
  mapId?: string;
  nodeId?: string;
  runId?: string;
}

// Cache user_id per tab to avoid getSession() per LLM call (50ms+ each)
let cachedUserId: string | null | undefined = undefined;
async function getCurrentUserId(): Promise<string | null> {
  if (cachedUserId !== undefined) return cachedUserId;
  try {
    const { data } = await supabase.auth.getSession();
    cachedUserId = data.session?.user?.id ?? null;
  } catch {
    cachedUserId = null;
  }
  return cachedUserId;
}

// Rate-limited stepless-call warning — log raz per provider:model żeby nie zalać
// console w batchu 290 nodów (każdy ~10 LLM calls = potencjalnie 2900 warningów).
const warnedSteplessKeys = new Set<string>();

/**
 * Fire-and-forget insert do llm_call_events. Nie blokuje calli, nie rzuca błędów.
 *
 * Defense-in-depth: jeśli caller pominął `step`, log warning RAZ per
 * provider:model — sygnał regresji w forensics layer (BUG #2 watchdog).
 */
export function trackLlmCall(event: LlmCallEvent): void {
  if (!event.step) {
    const key = `${event.provider}:${event.model}`;
    if (!warnedSteplessKeys.has(key)) {
      warnedSteplessKeys.add(key);
      logDebug(
        "costTracker",
        `WARN: LLM call without step (provider=${event.provider}, model=${event.model}) — caller missing forensics context. Shown once per provider:model.`,
      );
    }
  }

  void (async () => {
    try {
      const userId = await getCurrentUserId();
      const { error } = await (supabase as any).from("llm_call_events").insert({
        provider: event.provider,
        model: event.model,
        step: event.step ?? null,
        tokens_input: event.tokensInput,
        tokens_output: event.tokensOutput,
        cached_tokens: event.cachedTokens ?? 0,
        reasoning_tokens: event.reasoningTokens ?? 0,
        cost_usd: event.costUsd,
        duration_ms: event.durationMs ?? null,
        map_id: event.mapId ?? null,
        node_id: event.nodeId ?? null,
        run_id: event.runId ?? null,
        user_id: userId,
      });
      if (error) {
        logDebug("costTracker", `insert failed: ${error.message}`);
      }
    } catch (err) {
      logDebug("costTracker", `exception: ${err instanceof Error ? err.message : String(err)}`);
    }
  })();
}
